import * as amplitude from '@amplitude/analytics-browser';
import { SettingsBenefit } from '@spo/logic';
import { VpLoader } from '@vtmn-play/react';
import { VpIcon } from '@vtmn-play/react/headless';
import { VtmnAlert, VtmnTabs, VtmnTabsItem } from '@vtmn/react';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePatchProductNatureSettings } from '../../../hooks/mutations/usePatchProductNatureConfig';
import { useProductNatureData } from '../../../hooks/useProductNatures';
import useSessionState from '../../../hooks/useSessionState';
import { displayDate } from '../../../utils/date';
import { DrawerSettings } from './drawerTabSettings/DrawerSettings';
import { DrawerTabSettings } from './drawerTabSettings/DrawerTabSettings';
import { DrawerTabUserNeed } from './drawerTabUserNeed/DrawerTabUserNeed';

import { SpoNatureFullResponseSettingsItem } from '@spo/types';
import { noSegmentationOption } from '../../select/usageSegmentation';
import './drawer-nature.scss';

export const emptyBenefit: SettingsBenefit = {
  benefitId: null,
  index: 0,
};

export const DrawerNature = ({
  natureId,
  sportId,
  isOpen,
  closeDrawer,
}: {
  natureId: string;
  sportId: number;
  isOpen: boolean;
  closeDrawer: () => void;
}) => {
  const [tabViewIndex, setTabViewIndex] = useSessionState<number>(
    'architecture_drawer_tab',
    0,
  );
  const [openDrawerAddSettings, setOpenDrawerAddSettings] = useState(false);
  const [formIsEmpty, setFormIsEmpty] = useState(true);
  const [openExitSettingsModal, setOpenExitSettingsModal] = useState(false);
  const [selectedUsageSeg, setSelectedUsageSeg] = useState<number | null>(null);
  const [existingUsageSeg, setExistingUsageSeg] = useState('');
  const [productBenefits, setProductBenefits] = useState<SettingsBenefit[]>([
    emptyBenefit,
  ]);

  const { t } = useTranslation();

  const mutationSuccess = () => {
    setOpenDrawerAddSettings(false);
    setOpenExitSettingsModal(false);
    setProductBenefits([emptyBenefit]);
    setSelectedUsageSeg(null);
    refetchNatureData();
  };

  const {
    data: natureData,
    refetch: refetchNatureData,
    isFetching: isFetchingNatureData,
  } = useProductNatureData(natureId, sportId);

  const noSettings = natureData?.settings.length === 0;

  const {
    mutate,
    isPending: isFetchingNatureSettingsData,
    isError,
  } = usePatchProductNatureSettings(
    selectedUsageSeg,
    productBenefits,
    natureId,
    sportId,
    mutationSuccess,
  );

  const handleCloseDrawer = () => {
    if (openDrawerAddSettings || !formIsEmpty) {
      setOpenExitSettingsModal(true);
    } else {
      closeDrawer();
      setTabViewIndex(0);
      setFormIsEmpty(true);
      setExistingUsageSeg('');
    }
  };

  const editExistingSettings = (
    settings: SpoNatureFullResponseSettingsItem[],
  ) => {
    setProductBenefits(
      settings[0].productBenefits.map((pBene, index) => ({
        index,
        benefitId: pBene.id,
      })),
    );
    const lastSegFilled = settings[0].usageSegmentationTypes;
    if (lastSegFilled?.length === 0 || lastSegFilled[0]?.id === 99999) {
      setExistingUsageSeg(noSegmentationOption.label);
      setSelectedUsageSeg(noSegmentationOption.value);
    } else if (lastSegFilled[0].id !== 99999) {
      setExistingUsageSeg(`${lastSegFilled[0].id} - ${lastSegFilled[0].label}`);
      setSelectedUsageSeg(lastSegFilled[0].id);
    }
    setOpenDrawerAddSettings(true);
  };

  return (
    <div
      data-testid="drawer-nature"
      className={clsx('drawer-nature-container', { isOpen })}
    >
      <div className="drawer-nature-content">
        {natureData ? (
          <>
            <div className="drawer-title">
              {natureData?.externalReferential.name}
            </div>
            <div className="drawer-body">
              <div className="drawer-subtitles">
                {`#${natureData?.externalReferential.id}`}
                <span>
                  <VpIcon name="refresh" size={16} />
                  {`${t('last-update')} ${displayDate(natureData.updatedAt)}`}
                </span>
              </div>
              <div className="drawer-body-content">
                <VtmnTabs align="start">
                  <VtmnTabsItem
                    data-testid="tab-bar_settings"
                    aria-label="settings tab"
                    onClick={() => {
                      setTabViewIndex(0);
                      amplitude.track('Architecture_Drawer_Settings-Tab');
                    }}
                    aria-selected={tabViewIndex === 0}
                  >
                    {t('architecture.drawer.settings.tab-label')}
                  </VtmnTabsItem>
                  <VtmnTabsItem
                    className={clsx({ 'tab-disabled': noSettings })}
                    data-testid="tab-bar_user-need"
                    aria-label="user need tab"
                    onClick={() => {
                      setTabViewIndex(1);
                      amplitude.track('Architecture_Drawer_User-Need-Tab');
                    }}
                    aria-selected={tabViewIndex === 1}
                    disabled={noSettings}
                    aria-disabled={noSettings}
                  >
                    {t('architecture.drawer.user-need.tab-label')}
                  </VtmnTabsItem>
                </VtmnTabs>
                {isFetchingNatureData && <VpLoader size="extra-large" />}
                {!isFetchingNatureData && tabViewIndex === 0 && (
                  <DrawerTabSettings
                    nature={natureData}
                    editExistingSettings={() =>
                      editExistingSettings(natureData.settings)
                    }
                    setOpenDrawerAddSettings={setOpenDrawerAddSettings}
                  />
                )}
                {!isFetchingNatureData && tabViewIndex === 1 && (
                  <DrawerTabUserNeed
                    nature={natureData}
                    openExitSettingsModal={openExitSettingsModal}
                    setOpenExitSettingsModal={setOpenExitSettingsModal}
                    setFormIsEmpty={setFormIsEmpty}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <VpLoader size="large" />
        )}
      </div>
      {openDrawerAddSettings && natureData && (
        <>
          <DrawerSettings
            openExitSettingsModal={openExitSettingsModal}
            setOpenExitSettingsModal={setOpenExitSettingsModal}
            setOpenDrawerAddSettings={setOpenDrawerAddSettings}
            nature={natureData}
            selectedUsageSeg={selectedUsageSeg}
            setSelectedUsageSeg={setSelectedUsageSeg}
            productBenefits={productBenefits}
            setProductBenefits={setProductBenefits}
            existingUsageSeg={existingUsageSeg}
            setExistingUsageSeg={setExistingUsageSeg}
            mutate={mutate}
            isFetching={isFetchingNatureSettingsData}
          />
          {isError && (
            <VtmnAlert
              title={t('architecture.update-product.error')}
              variant="danger"
              ariaLabelCloseButton="Close"
              timeout={4500}
              data-testid="update-product-error"
            />
          )}
        </>
      )}
      <input
        className="drawer-backdrop"
        data-testid="drawer-backdrop"
        onClick={handleCloseDrawer}
        onKeyDown={handleCloseDrawer}
        type="button"
        tabIndex={0}
        aria-label="drawer backdrop"
      />
    </div>
  );
};
